import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Download, Eye, Loader2, RotateCcw, Save, Smartphone, Upload } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isCurrentUserAdmin } from '../utils/adminCheck';
import PhonePreview from './PhonePreview';
import './AppDesignStudio.css';

// ── Mobile App Theme Config (exact schema from theme.config.ts) ──
const DEFAULT_THEME = {
  colors: {
    background: '#F3F4F6',
    backgroundElevated: '#FFFFFF',
    surface: '#FFFFFF',
    surfaceAlt: '#F1F5F9',
    border: '#D1D5DB',
    borderStrong: '#9CA3AF',
    primary: '#111827',
    primaryDark: '#030712',
    gold: '#B45309',
    goldSoft: '#FEF3C7',
    textPrimary: '#111827',
    textSecondary: '#4B5563',
    textMuted: '#6B7280',
    textInverse: '#FFFFFF',
    success: '#11845B',
    warning: '#B45309',
    danger: '#B91C1C',
    accent: '#4F46E5',
    accentLight: '#EEF2FF',
    sold: '#E5E7EB',
    stage: '#0F0F1A',
    priceBadgeBg: '#111827',
    priceBadgeText: '#FFFFFF',
    wheelchair: '#2563EB',
    gradientPrimary: ['#374151', '#111827'],
    gradientGold: ['#FEF3C7', '#FDE68A'],
    gradientDark: ['#283246', '#111827'],
    gradientHero: ['#F8FAFC', '#EEF2F7'],
  },
  typography: { baseSize: 14, headingSize: 28, fontFamily: 'System' },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48, seatSize: 28, seatGapH: 6, seatGapV: 8, rowGap: 12, sectionPadding: 16, screenPadding: 20 },
  shadows: {
    card: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.12, shadowRadius: 18, elevation: 4 },
    glow: { shadowColor: '#111827', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.18, shadowRadius: 18, elevation: 6 },
    seat: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 2, elevation: 1 },
    selected: { shadowColor: '#4F46E5', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 5 },
    badge: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  },
  radii: { sm: 10, md: 16, lg: 24, xl: 32, pill: 999, button: 14 },
};

// ── Mobile App Strings (from LanguageContext.tsx DICT) ──
const DEFAULT_STRINGS = {
  'tab.home': { en: 'Home', ar: 'الرئيسية' },
  'tab.tickets': { en: 'My Tickets', ar: 'تذاكري' },
  'tab.info': { en: 'Festival Info', ar: 'معلومات المهرجان' },
  'tab.profile': { en: 'Profile', ar: 'الملف الشخصي' },
  'home.buyFestivalTickets': { en: 'Buy Festival Tickets', ar: 'شراء تذاكر المهرجان' },
  'home.buyFestivalSub': { en: 'June 19–21 festival admission', ar: 'دخول المهرجان من 19 إلى 21 يونيو' },
  'home.buyConcertTickets': { en: 'Buy Concert Tickets', ar: 'شراء تذاكر الحفل' },
  'home.buyConcertSub': { en: 'Pacific Amphitheatre reserved seats', ar: 'مقاعد محجوزة في مسرح باسيفيك' },
  'home.myTickets': { en: 'My Tickets', ar: 'تذاكري' },
  'home.showDays': { en: 'Show Days', ar: 'أيام العروض' },
  'home.maps': { en: 'Maps', ar: 'الخرائط' },
  'home.ocFair': { en: 'OC FAIR', ar: 'معرض OC' },
  'home.performing': { en: "Who's Performing", ar: 'الفنانون المشاركون' },
  'home.seeAll': { en: 'See all', ar: 'عرض الكل' },
  'home.pacificAmp': { en: 'Pacific Amphitheatre', ar: 'مسرح باسيفيك' },
  'home.festivalLineup': { en: 'Festival Grounds Lineup', ar: 'فنانو أرض المهرجان' },
  'home.concertNights': { en: 'Concert Nights', ar: 'ليالي الحفلات' },
  'home.venue': { en: 'Venue', ar: 'الموقع' },
  'home.directions': { en: 'Directions', ar: 'الاتجاهات' },
  'home.headliner': { en: 'HEADLINER', ar: 'النجم الرئيسي' },
  'home.artists': { en: 'artists', ar: 'فنانين' },
  'info.title': { en: 'Festival Info', ar: 'معلومات المهرجان' },
  'info.lineup': { en: 'Lineup', ar: 'البرنامج الفني' },
  'info.venue': { en: 'Venue', ar: 'الموقع' },
  'info.general': { en: 'General', ar: 'عام' },
  'info.festivalGroundArtists': { en: 'Festival Grounds Artists', ar: 'فنانو أرض المهرجان' },
  'info.ampArtists': { en: 'Pacific Amphitheatre Concert Artists', ar: 'فنانو حفلات مسرح باسيفيك' },
  'info.generalInfo': { en: 'General Info', ar: 'معلومات عامة' },
  'info.codeOfConduct': { en: 'Code of Conduct', ar: 'مدونة السلوك' },
  'info.healthSafety': { en: 'Health & Safety', ar: 'الصحة والسلامة' },
  'info.hours': { en: 'Hours', ar: 'ساعات العمل' },
  'info.openInMaps': { en: 'Open in Maps', ar: 'فتح في الخرائط' },
  'info.parkingOpens': { en: 'Parking Opens', ar: 'فتح المواقف' },
  'info.groundsOpen': { en: 'Grounds Open', ar: 'فتح أرض المهرجان' },
  'info.groundsClose': { en: 'Grounds Close', ar: 'إغلاق أرض المهرجان' },
  'info.ampNotice': { en: 'Amphitheatre tickets are sold separately, and each amphitheatre ticket includes same-day festival admission.', ar: 'تذاكر المسرح تُباع بشكل منفصل، وكل تذكرة تشمل دخول المهرجان في نفس اليوم.' },
  'tickets.title': { en: 'My Tickets', ar: 'تذاكري' },
  'tickets.transfers': { en: 'Transfers', ar: 'التحويلات' },
  'tickets.activeTab': { en: 'Active', ar: 'نشطة' },
  'tickets.otherTab': { en: 'Other', ar: 'أخرى' },
  'tickets.noActive': { en: 'No active tickets', ar: 'لا توجد تذاكر نشطة' },
  'tickets.noArchived': { en: 'No archived tickets', ar: 'لا توجد تذاكر مؤرشفة' },
  'tickets.activeDesc': { en: 'Valid tickets you purchase on ocmenafestival.com will appear here automatically.', ar: 'ستظهر هنا تلقائيًا التذاكر الصالحة التي تشتريها من ocmenafestival.com.' },
  'tickets.otherDesc': { en: 'Used, refunded, voided, or cancelled tickets will appear in this tab.', ar: 'ستظهر في هذا التبويب التذاكر المستخدمة أو المستردة أو الملغاة.' },
  'tickets.buyTickets': { en: 'Buy Tickets', ar: 'شراء التذاكر' },
  'tickets.festivalPasses': { en: 'Festival Passes', ar: 'تصاريح المهرجان' },
  'tickets.pacAmp': { en: 'PAC AMP Concerts', ar: 'حفلات PAC AMP' },
  'tickets.vendorBooths': { en: 'Vendor Booths', ar: 'أكشاك البائعين' },
  'tickets.vipParking': { en: 'VIP Parking', ar: 'مواقف VIP' },
  'tickets.other': { en: 'Other', ar: 'أخرى' },
  'tickets.statusActive': { en: 'Active', ar: 'نشطة' },
  'tickets.statusUsed': { en: 'Used', ar: 'مستخدمة' },
  'tickets.statusPending': { en: 'Pending', ar: 'قيد الانتظار' },
  'profile.title': { en: 'Profile', ar: 'الملف الشخصي' },
  'profile.buyConcert': { en: 'Buy Concert Tickets', ar: 'شراء تذاكر الحفل' },
  'profile.opensWebsite': { en: 'Opens ocmenafestival.com', ar: 'يفتح ocmenafestival.com' },
  'profile.accountSettings': { en: 'Account Settings', ar: 'إعدادات الحساب' },
  'profile.accountSettingsSub': { en: 'Edit profile, change password', ar: 'تعديل الملف وتغيير كلمة المرور' },
  'profile.notifications': { en: 'Notifications', ar: 'الإشعارات' },
  'profile.notificationsSub': { en: 'View your alerts and updates', ar: 'عرض التنبيهات والتحديثات' },
  'profile.transferHistory': { en: 'Transfer History', ar: 'سجل التحويلات' },
  'profile.transferHistorySub': { en: "See tickets you've sent or received", ar: 'عرض التذاكر المرسلة أو المستلمة' },
  'profile.logout': { en: 'Log Out', ar: 'تسجيل الخروج' },
  'profile.logoutConfirmTitle': { en: 'Log Out', ar: 'تسجيل الخروج' },
  'profile.logoutConfirmBody': { en: 'Are you sure you want to log out?', ar: 'هل أنت متأكد أنك تريد تسجيل الخروج؟' },
  'profile.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'profile.language': { en: 'Language', ar: 'اللغة' },
  'profile.languageSub': { en: 'Switch between English and Arabic', ar: 'التبديل بين الإنجليزية والعربية' },
  'profile.biometricLogin': { en: 'Login', ar: 'تسجيل الدخول' },
  'profile.biometricSub': { en: 'Unlock the app instantly next time', ar: 'افتح التطبيق فورًا في المرة القادمة' },
  'ticket.notFound': { en: 'Ticket not found.', ar: 'التذكرة غير موجودة.' },
  'ticket.goBack': { en: 'Go back', ar: 'العودة' },
  'ticket.transfers': { en: 'Transfers', ar: 'التحويلات' },
  'ticket.transfer': { en: 'Transfer Ticket', ar: 'تحويل التذكرة' },
  'ticket.sendInstant': { en: 'Send instantly to any email', ar: 'أرسل فورًا لأي بريد إلكتروني' },
  'ticket.giftLabel': { en: 'Gift label (optional)', ar: 'اسم الهدية (اختياري)' },
  'ticket.emailPlaceholder': { en: 'recipient@email.com', ar: 'recipient@email.com' },
  'ticket.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'ticket.sendNow': { en: 'Send Now', ar: 'إرسال الآن' },
  'ticket.transferHint': { en: 'Sent instantly — no pending step. Gift label is optional and for easy naming.', ar: 'يتم الإرسال فورًا بدون انتظار. اسم الهدية اختياري للتنظيم.' },
  'ticket.status': { en: 'Status', ar: 'الحالة' },
  'ticket.validDays': { en: 'Valid Days', ar: 'الأيام الصالحة' },
  'ticket.section': { en: 'Section', ar: 'القسم' },
  'ticket.row': { en: 'Row', ar: 'الصف' },
  'ticket.seat': { en: 'Seat', ar: 'المقعد' },
  'ticket.added': { en: 'Added', ar: 'تاريخ الإضافة' },
  'ticket.presentQr': { en: 'Present this QR code at the gate for entry.', ar: 'اعرض رمز QR هذا عند البوابة للدخول.' },
  'ticket.ticketSent': { en: 'Ticket Sent', ar: 'تم إرسال التذكرة' },
  'ticket.transferFailed': { en: 'Transfer Failed', ar: 'فشل التحويل' },
  'ticket.validEmail': { en: 'Enter a valid email address.', ar: 'أدخل بريدًا إلكترونيًا صالحًا.' },
};

// ── Section field definitions ──
const colorFields = [
  ['background', 'App Background'], ['backgroundElevated', 'Elevated Background'], ['surface', 'Card Surface'],
  ['surfaceAlt', 'Alt Surface'], ['border', 'Border Color'], ['borderStrong', 'Strong Border'],
  ['primary', 'Primary / Brand'], ['primaryDark', 'Primary Dark'], ['gold', 'Gold Accent'],
  ['goldSoft', 'Gold Soft BG'], ['textPrimary', 'Primary Text'], ['textSecondary', 'Secondary Text'],
  ['textMuted', 'Muted Text'], ['textInverse', 'Inverse Text'], ['accent', 'Accent Purple'],
  ['accentLight', 'Accent Light BG'], ['success', 'Success Green'], ['warning', 'Warning Amber'],
  ['danger', 'Danger Red'], ['sold', 'Sold Out Grey'], ['stage', 'Stage Text'],
  ['priceBadgeBg', 'Price Badge BG'], ['priceBadgeText', 'Price Badge Text'], ['wheelchair', 'Wheelchair Icon'],
];

const gradientFields = [
  ['gradientPrimary', 'Primary Gradient'], ['gradientGold', 'Gold Gradient'],
  ['gradientDark', 'Dark Gradient'], ['gradientHero', 'Hero Gradient'],
];

const numberFieldDefs = {
  typography: [['baseSize', 'Base Font Size', 8, 32], ['headingSize', 'Heading Font Size', 16, 48]],
  spacing: [['xs', 'Extra Small', 0, 16], ['sm', 'Small', 0, 24], ['md', 'Medium', 0, 32], ['lg', 'Large', 0, 40], ['xl', 'Extra Large', 0, 48], ['xxl', 'XXL', 0, 64], ['seatSize', 'Seat Size', 16, 48], ['seatGapH', 'Seat Gap H', 0, 20], ['seatGapV', 'Seat Gap V', 0, 20], ['rowGap', 'Row Gap', 0, 24], ['sectionPadding', 'Section Padding', 0, 32], ['screenPadding', 'Screen Padding', 0, 32]],
  radii: [['sm', 'Small Radius', 0, 24], ['md', 'Medium Radius', 0, 32], ['lg', 'Large Radius', 0, 40], ['xl', 'Extra Large', 0, 48], ['button', 'Button Radius', 0, 32]],
};

const shadowDefs = [['card', 'Card Shadow'], ['glow', 'Glow Shadow'], ['seat', 'Seat Shadow'], ['selected', 'Selected Shadow'], ['badge', 'Badge Shadow']];

const sections = [
  { key: 'colors', label: 'Colors', count: colorFields.length },
  { key: 'gradients', label: 'Gradients', count: gradientFields.length },
  { key: 'typography', label: 'Typography', count: numberFieldDefs.typography.length },
  { key: 'spacing', label: 'Spacing', count: numberFieldDefs.spacing.length },
  { key: 'shadows', label: 'Shadows', count: shadowDefs.length },
  { key: 'radii', label: 'Corner Radii', count: numberFieldDefs.radii.length },
  { key: 'strings', label: 'App Strings', count: Object.keys(DEFAULT_STRINGS).length },
];

function deepMerge(base, override) {
  if (!override || typeof override !== 'object') return base;
  const result = { ...base };
  for (const [key, val] of Object.entries(override)) {
    if (val && typeof val === 'object' && !Array.isArray(val) && base[key] && typeof base[key] === 'object') {
      result[key] = deepMerge(base[key], val);
    } else if (val !== null && val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}

// ── Field components ──
function ColorField({ field, label, value, onChange }) {
  return (
    <label className="ads-color-field">
      <span className="ads-color-copy"><span>{label}</span><code>{value}</code></span>
      <span className="ads-color-control">
        <input type="color" value={value.slice(0, 7)} onChange={(e) => onChange(field, e.target.value.toUpperCase())} />
        <input value={value} onChange={(e) => onChange(field, e.target.value)} aria-label={`${label} color value`} />
      </span>
    </label>
  );
}

function NumberField({ field, label, value, min, max, onChange }) {
  return (
    <label className="ads-range-field">
      <span><strong>{label}</strong><output>{value}px</output></span>
      <input type="range" min={min} max={max} step={1} value={value} onChange={(e) => onChange(field, Number(e.target.value))} />
    </label>
  );
}

function ShadowField({ name, label, shadow, onChange }) {
  return (
    <div className="ads-shadow-field">
      <div className="ads-shadow-header"><strong>{label}</strong></div>
      <label className="ads-range-field">
        <span><strong>Opacity</strong><output>{shadow.shadowOpacity}</output></span>
        <input type="range" min={0} max={1} step={0.01} value={shadow.shadowOpacity} onChange={(e) => onChange(name, { ...shadow, shadowOpacity: Number(e.target.value) })} />
      </label>
      <label className="ads-range-field">
        <span><strong>Radius</strong><output>{shadow.shadowRadius}px</output></span>
        <input type="range" min={0} max={30} step={1} value={shadow.shadowRadius} onChange={(e) => onChange(name, { ...shadow, shadowRadius: Number(e.target.value) })} />
      </label>
      <label className="ads-range-field">
        <span><strong>Offset Y</strong><output>{shadow.shadowOffset.height}px</output></span>
        <input type="range" min={-20} max={20} step={1} value={shadow.shadowOffset.height} onChange={(e) => onChange(name, { ...shadow, shadowOffset: { ...shadow.shadowOffset, height: Number(e.target.value) } })} />
      </label>
      <label className="ads-range-field">
        <span><strong>Elevation (Android)</strong><output>{shadow.elevation}</output></span>
        <input type="range" min={0} max={12} step={1} value={shadow.elevation} onChange={(e) => onChange(name, { ...shadow, elevation: Number(e.target.value) })} />
      </label>
      <label className="ads-color-field">
        <span className="ads-color-copy"><span>Shadow Color</span><code>{shadow.shadowColor}</code></span>
        <span className="ads-color-control">
          <input type="color" value={shadow.shadowColor.slice(0, 7)} onChange={(e) => onChange(name, { ...shadow, shadowColor: e.target.value.toUpperCase() })} />
          <input value={shadow.shadowColor} onChange={(e) => onChange(name, { ...shadow, shadowColor: e.target.value })} aria-label="Shadow color" />
        </span>
      </label>
    </div>
  );
}

function StringRow({ strKey, entry, onChange }) {
  return (
    <div className="ads-string-row">
      <code className="ads-string-key">{strKey}</code>
      <div className="ads-string-inputs">
        <input value={entry.en} onChange={(e) => onChange(strKey, 'en', e.target.value)} placeholder="English" />
        <input value={entry.ar} onChange={(e) => onChange(strKey, 'ar', e.target.value)} placeholder="Arabic" dir="rtl" />
      </div>
    </div>
  );
}

export default function AppDesignStudio() {
  const navigate = useNavigate();
  const { loading } = useAuth();
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [savedTheme, setSavedTheme] = useState(DEFAULT_THEME);
  const [strings, setStrings] = useState(DEFAULT_STRINGS);
  const [savedStrings, setSavedStrings] = useState(DEFAULT_STRINGS);
  const [activeSection, setActiveSection] = useState('colors');
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const dirty = useMemo(() => JSON.stringify(theme) !== JSON.stringify(savedTheme) || JSON.stringify(strings) !== JSON.stringify(savedStrings), [theme, savedTheme, strings, savedStrings]);

  useEffect(() => {
    if (loading || !isCurrentUserAdmin()) return;
    api.getAdminCmsContent().then((response) => {
      const remoteTheme = response?.data?.mobileThemeConfig;
      const remoteStrings = response?.data?.mobileAppStrings;
      if (remoteTheme && typeof remoteTheme === 'object') {
        const merged = deepMerge(DEFAULT_THEME, remoteTheme);
        setTheme(merged);
        setSavedTheme(merged);
      }
      if (remoteStrings && typeof remoteStrings === 'object') {
        const mergedStrings = { ...DEFAULT_STRINGS };
        for (const [key, val] of Object.entries(remoteStrings)) {
          if (mergedStrings[key] && val && typeof val === 'object') {
            mergedStrings[key] = { ...mergedStrings[key], ...val };
          }
        }
        setStrings(mergedStrings);
        setSavedStrings(mergedStrings);
      }
    }).catch((error) => setMessage(error.message || 'Could not load saved design.')).finally(() => setLoadingData(false));
  }, [loading]);

  if (!loading && !isCurrentUserAdmin()) return <Navigate to="/login?redirect=/admin/app-design" replace />;

  const updateColor = (field, value) => setTheme((c) => ({ ...c, colors: { ...c.colors, [field]: value } }));
  const updateGradient = (field, index, value) => setTheme((c) => {
    const arr = [...(c.colors[field] || ['#000000', '#000000'])];
    arr[index] = value;
    return { ...c, colors: { ...c.colors, [field]: arr } };
  });
  const updateNumber = (section, field, value) => setTheme((c) => ({ ...c, [section]: { ...c[section], [field]: value } }));
  const updateShadow = (name, value) => setTheme((c) => ({ ...c, shadows: { ...c.shadows, [name]: value } }));
  const updateString = (strKey, lang, value) => setStrings((c) => ({ ...c, [strKey]: { ...c[strKey], [lang]: value } }));

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      await api.saveCmsContent({ mobileThemeConfig: theme, mobileAppStrings: strings });
      setSavedTheme(theme); setSavedStrings(strings);
      setMessage('Published. Mobile app will sync these settings on next launch.');
    } catch (error) { setMessage(error.message || 'Could not publish.'); }
    finally { setSaving(false); }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ mobileThemeConfig: theme, mobileAppStrings: strings }, null, 2)], { type: 'application/json' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `ocmena-mobile-design-${new Date().toISOString().slice(0, 10)}.json`; link.click(); URL.revokeObjectURL(link.href);
  };
  const importJson = (event) => {
    const file = event.target.files?.[0]; if (!file) return;
    file.text().then((text) => {
      const data = JSON.parse(text);
      if (data.mobileThemeConfig) setTheme(deepMerge(DEFAULT_THEME, data.mobileThemeConfig));
      if (data.mobileAppStrings) setStrings((c) => ({ ...c, ...data.mobileAppStrings }));
      setMessage('Imported as unpublished draft.');
    }).catch(() => setMessage('Invalid JSON file.'));
  };

  const resetSection = () => {
    if (activeSection === 'colors') setTheme((c) => ({ ...c, colors: { ...c.colors, ...DEFAULT_THEME.colors } }));
    else if (activeSection === 'gradients') setTheme((c) => ({ ...c, colors: { ...c.colors, gradientPrimary: DEFAULT_THEME.colors.gradientPrimary, gradientGold: DEFAULT_THEME.colors.gradientGold, gradientDark: DEFAULT_THEME.colors.gradientDark, gradientHero: DEFAULT_THEME.colors.gradientHero } }));
    else if (activeSection === 'typography') setTheme((c) => ({ ...c, typography: DEFAULT_THEME.typography }));
    else if (activeSection === 'spacing') setTheme((c) => ({ ...c, spacing: DEFAULT_THEME.spacing }));
    else if (activeSection === 'shadows') setTheme((c) => ({ ...c, shadows: DEFAULT_THEME.shadows }));
    else if (activeSection === 'radii') setTheme((c) => ({ ...c, radii: DEFAULT_THEME.radii }));
    else if (activeSection === 'strings') setStrings(DEFAULT_STRINGS);
  };

  const currentSection = sections.find((s) => s.key === activeSection);

  return (
    <div className="ads-shell">
      <header className="ads-topbar">
        <div className="ads-brand">
          <button onClick={() => navigate('/admin')}><ChevronLeft size={20} /></button>
          <Smartphone size={22} />
          <span><strong>Mobile App Design Studio</strong><small>Edit mobile app theme, colors, sizes & text</small></span>
        </div>
        <div className="ads-actions">
          <button onClick={() => { setTheme(savedTheme); setStrings(savedStrings); }} disabled={!dirty}><RotateCcw size={16} /> Undo</button>
          <label><Upload size={16} /> Import<input type="file" accept="application/json" onChange={importJson} /></label>
          <button onClick={exportJson}><Download size={16} /> Export</button>
          <button className="primary" onClick={save} disabled={!dirty || saving}>{saving ? <Loader2 className="spin" size={17} /> : <Save size={17} />} Publish</button>
        </div>
      </header>
      <div className="ads-workspace">
        <aside className="ads-sidebar">
          <div className="ads-sidebar-title"><strong>Mobile App Theme</strong><small>OCMENA React Native app</small></div>
          <nav className="ads-nav-group">
            {sections.map((s) => (
              <button key={s.key} className={activeSection === s.key ? 'active' : ''} onClick={() => setActiveSection(s.key)}>
                {s.label}<span>{s.count}</span>
              </button>
            ))}
          </nav>
          <div className="ads-safety"><Check size={16} /><span><strong>Backend persisted</strong><small>All changes save to CMS. Mobile app syncs automatically.</small></span></div>
        </aside>
        <section className="ads-controls">
          <header>
            <div>
              <small>MOBILE APP</small>
              <h1>{currentSection?.label}</h1>
              {activeSection === 'strings'
                ? <p>Edit all text shown in the mobile app. Supports English & Arabic.</p>
                : <p>Directly edits the mobile app's <code>ThemeConfig</code>. Changes apply on next app sync.</p>}
            </div>
            {dirty && <span className="ads-draft">Unpublished draft</span>}
          </header>
          {loadingData ? <div className="ads-loading"><Loader2 className="spin" /> Loading…</div> : (
            <>
              {activeSection === 'colors' && (
                <div className="ads-field-list">
                  {colorFields.map(([field, label]) => (
                    <ColorField key={field} field={field} label={label} value={theme.colors[field]} onChange={updateColor} />
                  ))}
                </div>
              )}
              {activeSection === 'gradients' && (
                <div className="ads-field-list">
                  {gradientFields.map(([field, label]) => (
                    <div key={field} className="ads-shadow-field">
                      <div className="ads-shadow-header"><strong>{label}</strong></div>
                      <ColorField field={`${field}[0]`} label="Start Color" value={theme.colors[field]?.[0] || '#000000'} onChange={(_, v) => updateGradient(field, 0, v)} />
                      <ColorField field={`${field}[1]`} label="End Color" value={theme.colors[field]?.[1] || '#000000'} onChange={(_, v) => updateGradient(field, 1, v)} />
                    </div>
                  ))}
                </div>
              )}
              {activeSection === 'typography' && (
                <div className="ads-field-list">
                  {numberFieldDefs.typography.map(([field, label, min, max]) => (
                    <NumberField key={field} field={field} label={label} value={theme.typography[field]} min={min} max={max} onChange={(f, v) => updateNumber('typography', f, v)} />
                  ))}
                </div>
              )}
              {activeSection === 'spacing' && (
                <div className="ads-field-list">
                  {numberFieldDefs.spacing.map(([field, label, min, max]) => (
                    <NumberField key={field} field={field} label={label} value={theme.spacing[field]} min={min} max={max} onChange={(f, v) => updateNumber('spacing', f, v)} />
                  ))}
                </div>
              )}
              {activeSection === 'shadows' && (
                <div className="ads-field-list">
                  {shadowDefs.map(([name, label]) => (
                    <ShadowField key={name} name={name} label={label} shadow={theme.shadows[name]} onChange={updateShadow} />
                  ))}
                </div>
              )}
              {activeSection === 'radii' && (
                <div className="ads-field-list">
                  {numberFieldDefs.radii.map(([field, label, min, max]) => (
                    <NumberField key={field} field={field} label={label} value={theme.radii[field]} min={min} max={max} onChange={(f, v) => updateNumber('radii', f, v)} />
                  ))}
                </div>
              )}
              {activeSection === 'strings' && (
                <div className="ads-string-list">
                  {Object.entries(strings).map(([key, entry]) => (
                    <StringRow key={key} strKey={key} entry={entry} onChange={updateString} />
                  ))}
                </div>
              )}
            </>
          )}
          <button className="ads-reset" onClick={resetSection}>
            <RotateCcw size={15} /> Reset {currentSection?.label.toLowerCase()}
          </button>
          {message && <div className="ads-message">{message}</div>}
        </section>
        <section className="ads-preview-panel">
          <header>
            <span><Eye size={17} /><strong>Live Preview</strong></span>
            <span className="ads-preview-badge">Instant render — no Expo needed</span>
          </header>
          <div className="ads-preview-stage">
            <div className="ads-phone-frame">
              <div className="ads-phone-notch" />
              <PhonePreview theme={theme} strings={strings} />
            </div>
          </div>
          <p>Every color, spacing, radius, shadow & text change updates <strong>instantly</strong>. Click <strong>Publish</strong> to push to the mobile app.</p>
        </section>
      </div>
    </div>
  );
}
